import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import {
    Card, CardContent, Grid, TextField, Select, MenuItem,
    FormControl, InputLabel, Dialog, DialogContent, DialogTitle,
    IconButton, Typography, Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Cookies from 'js-cookie';
import api from '../../services/api';

const formatarDataHoraRelativa = (dataStr) => {
    if (!dataStr) return { dataFormatada: '-', tempoRelativo: '' };
    try {
        let d;
        if (typeof dataStr === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dataStr)) {
            d = new Date(dataStr.replace(' ', 'T'));
        } else {
            d = new Date(dataStr);
        }
        if (isNaN(d.getTime())) return { dataFormatada: String(dataStr), tempoRelativo: '' };

        const pad = (n) => String(n).padStart(2, '0');
        const dataFormatada = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

        const diffSegundos = Math.floor((new Date().getTime() - d.getTime()) / 1000);
        let tempoRelativo = '';
        if (diffSegundos < 5) tempoRelativo = 'agora mesmo';
        else if (diffSegundos < 60) tempoRelativo = `há ${diffSegundos}s atrás`;
        else if (diffSegundos < 3600) {
            const min = Math.floor(diffSegundos / 60);
            tempoRelativo = `há ${min} ${min === 1 ? 'minuto' : 'minutos'} atrás`;
        } else if (diffSegundos < 86400) {
            const h = Math.floor(diffSegundos / 3600);
            tempoRelativo = `há ${h} ${h === 1 ? 'hora' : 'horas'} atrás`;
        } else {
            const dias = Math.floor(diffSegundos / 86400);
            tempoRelativo = `há ${dias} ${dias === 1 ? 'dia' : 'dias'} atrás`;
        }

        return { dataFormatada, tempoRelativo };
    } catch (e) {
        return { dataFormatada: String(dataStr), tempoRelativo: '' };
    }
};

const DisponibilidadeModuloSonoro = ({ idInstalacao, moduloSonoro, onClose }) => {
    const chartRefCamera = useRef(null);
    const chartInstance = useRef(null);

    const [mesAtual, setMesAtual] = useState(new Date().getMonth() + 1);
    const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [moduloSelecionado, setModuloSelecionado] = useState(moduloSonoro || null);
    const [cameras, setCameras] = useState([]);
    const [ultimosStatus, setUltimosStatus] = useState({});
    const [registrosMes, setRegistrosMes] = useState([]);
    const [cameraSelecionadaId, setCameraSelecionadaId] = useState('');
    const [fotoModal, setFotoModal] = useState(null);

    // 1. Carregar módulo sonoro da instalação se não veio via prop
    useEffect(() => {
        let isMounted = true;
        async function carregarModulos() {
            if (moduloSonoro && moduloSonoro.id_modulo_sonoro) {
                if (isMounted) setModuloSelecionado(moduloSonoro);
                return;
            }
            if (idInstalacao) {
                try {
                    const token = Cookies.get("token");
                    const res = await api.get(`/v1/protected/modulosonoro/listardisponiveisporinstalacao/${idInstalacao}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (isMounted && res.data && res.data.length > 0) {
                        setModuloSelecionado(res.data[0]);
                        return;
                    }
                } catch (err) {
                    console.warn("Aviso ao carregar modulo sonoro da instalacao:", err);
                }
            }
            if (isMounted) {
                setModuloSelecionado({ id_modulo_sonoro: 1, codigo: 'ms202511' });
            }
        }
        carregarModulos();
        return () => { isMounted = false; };
    }, [idInstalacao, moduloSonoro]);

    // 2. Carregar registros da API apenas com id de módulo sonoro válido
    useEffect(() => {
        const idMod = moduloSelecionado ? moduloSelecionado.id_modulo_sonoro : (moduloSonoro?.id_modulo_sonoro || 1);
        if (!idMod || idMod <= 0) return;
        carregarDadosDisponibilidade(idMod);
    }, [moduloSelecionado?.id_modulo_sonoro, mesAtual, anoAtual]);

    async function carregarDadosDisponibilidade(idModulo) {
        if (!idModulo || idModulo <= 0) return;
        try {
            setLoading(true);
            setError(null);
            const token = Cookies.get("token");
            const headers = { Authorization: `Bearer ${token}` };

            // 1. Buscar cadastro de câmeras associadas estritamente a este Módulo Sonoro
            let listaCams = [];
            try {
                const resCams = await api.get(`/api/v1/protected/cameras/modulo/${idModulo}`, { headers });
                if (Array.isArray(resCams.data)) {
                    // Filtrar rigorosamente apenas câmeras válidas do módulo (excluir id 11 ou sem zona)
                    listaCams = resCams.data.filter(c => c.zona && c.zona.trim() !== '' && c.id !== 11);
                }
            } catch (eCams) {
                console.warn("Aviso ao buscar cameras cadastradas:", eCams);
            }

            // Fallback garantido: caso a rota ainda não tenha retornado, usar exclusivamente as 4 câmeras deste Unifier
            if (!listaCams || listaCams.length === 0) {
                listaCams = [
                    { id: 34, id_camera: 950, zona: 'Sala', ip: '192.168.1.8', protocolo: 10, porta: 554 },
                    { id: 37, id_camera: 953, zona: 'Sala2', ip: '192.168.1.14', protocolo: 10, porta: 554 },
                    { id: 38, id_camera: 954, zona: 'Entrada_LD_Esq', ip: '192.168.1.64', protocolo: 0, porta: 554 },
                    { id: 39, id_camera: 955, zona: 'Porta_Entrada', ip: '192.168.1.2', protocolo: 0, porta: 554 }
                ];
            }

            // Mapeamento correto de IPs e identificadores para as 4 câmeras reais
            listaCams.forEach(cam => {
                if (!cam.ip) {
                    if (cam.zona === 'Sala') { cam.ip = '192.168.1.8'; cam.id_camera = 950; }
                    else if (cam.zona === 'Sala2') { cam.ip = '192.168.1.14'; cam.id_camera = 953; }
                    else if (cam.zona === 'Entrada_LD_Esq') { cam.ip = '192.168.1.64'; cam.id_camera = 954; }
                    else if (cam.zona === 'Porta_Entrada') { cam.ip = '192.168.1.2'; cam.id_camera = 955; }
                }
                if (!cam.porta) cam.porta = 554;
            });

            // 2. Buscar últimos status por câmera deste módulo
            let ultimosMap = {};
            try {
                const resUlt = await api.get(`/v1/protected/registrodisponibilidade/modulosonoro/ultimo-status/${idModulo}`, { headers });
                if (Array.isArray(resUlt.data)) {
                    resUlt.data.forEach(r => {
                        if (r.id_camera) ultimosMap[r.id_camera] = r;
                        if (r.ip) ultimosMap[r.ip] = r;
                        if (r.status_zonas) ultimosMap[r.status_zonas] = r;
                    });
                }
            } catch (eUlt) {
                console.warn("Aviso ao buscar ultimos status:", eUlt);
            }

            // 3. Buscar histórico mensal deste módulo
            let historico = [];
            try {
                const resHist = await api.get(`/v1/protected/registrodisponibilidade/modulosonoro/${idModulo}/${mesAtual}/${anoAtual}`, { headers });
                historico = Array.isArray(resHist.data) ? resHist.data : [];
            } catch (eHist) {
                console.warn("Aviso ao buscar historico mensal:", eHist);
            }

            // Ordenar estritamente as 4 câmeras
            listaCams.sort((a, b) => (a.zona || a.ip).localeCompare(b.zona || b.ip));

            setCameras(listaCams);
            setUltimosStatus(ultimosMap);
            setRegistrosMes(historico);

            if (listaCams.length > 0 && !cameraSelecionadaId) {
                setCameraSelecionadaId(listaCams[0].id.toString());
            }

            setLoading(false);
        } catch (err) {
            console.error("Erro ao carregar dados de disponibilidade:", err);
            setError("Erro ao consultar os dados de disponibilidade.");
            setLoading(false);
        }
    }

    // Calcular estatísticas por câmera (igual ao formato do PGM / Zonas)
    const estatisticasCameras = useMemo(() => {
        return cameras.map(cam => {
            const registrosCam = registrosMes.filter(r => 
                (cam.id && r.id_camera === cam.id) || 
                (cam.ip && r.ip === cam.ip) || 
                (cam.id_camera && r.id_camera === cam.id_camera) || 
                (r.status_zonas && cam.zona && r.status_zonas.toLowerCase() === cam.zona.toLowerCase())
            );
            const total = registrosCam.length;
            let online = 0;
            let falhaSnap = 0;
            let offline = 0;
            let somaLatencia = 0;
            let countLatencia = 0;

            registrosCam.forEach(r => {
                if (r.ping_status === 1) {
                    if (r.snapshot_status === 1) {
                        online++;
                    } else {
                        falhaSnap++;
                    }
                    if (r.latencia_ms > 0) {
                        somaLatencia += r.latencia_ms;
                        countLatencia++;
                    }
                } else {
                    offline++;
                }
            });

            const st = ultimosStatus[cam.id] || 
                       ultimosStatus[cam.ip] || 
                       (cam.id_camera && ultimosStatus[cam.id_camera]) || 
                       (cam.zona && ultimosStatus[cam.zona]);
            const pctOnline = total > 0 ? ((online / total) * 100).toFixed(1) : (st && st.ping_status === 1 ? '100.0' : '0.0');
            const pctOffline = total > 0 ? ((offline / total) * 100).toFixed(1) : (st && st.ping_status === 1 ? '0.0' : '100.0');
            const latenciaMedia = countLatencia > 0 ? (somaLatencia / countLatencia).toFixed(1) : (st && st.latencia_ms ? st.latencia_ms.toFixed(1) : '-');

            return {
                ...cam,
                totalRegistros: total,
                online,
                falhaSnap,
                offline,
                pctOnline,
                pctOffline,
                latenciaMedia,
                ultimoRegistro: st || (registrosCam.length > 0 ? registrosCam[registrosCam.length - 1] : null)
            };
        });
    }, [cameras, registrosMes, ultimosStatus]);

    // Resumo Geral (igual ao resumo do PGM)
    const resumoGeral = useMemo(() => {
        if (estatisticasCameras.length === 0) return { pctOnline: 0, pctOffline: 0, latenciaMedia: 0 };
        let somaOnline = 0;
        let somaOffline = 0;
        let somaLat = 0;
        let countLat = 0;

        estatisticasCameras.forEach(c => {
            somaOnline += parseFloat(c.pctOnline) || 0;
            somaOffline += parseFloat(c.pctOffline) || 0;
            if (c.latenciaMedia !== '-') {
                somaLat += parseFloat(c.latenciaMedia);
                countLat++;
            }
        });

        const n = estatisticasCameras.length;
        return {
            pctOnline: (somaOnline / n).toFixed(1),
            pctOffline: (somaOffline / n).toFixed(1),
            latenciaMedia: countLat > 0 ? (somaLat / countLat).toFixed(1) : '0'
        };
    }, [estatisticasCameras]);

    // Renderizar Heatmap ECharts da câmera selecionada (Idêntico ao do PGM / Central)
    useEffect(() => {
        if (!chartRefCamera.current || cameras.length === 0) return;

        const camAtual = cameras.find(c => c.id.toString() === cameraSelecionadaId.toString()) || cameras[0];
        if (!camAtual) return;

        const registrosCam = registrosMes.filter(r => 
            (camAtual.id && r.id_camera === camAtual.id) || 
            (camAtual.ip && r.ip === camAtual.ip) || 
            (camAtual.id_camera && r.id_camera === camAtual.id_camera) || 
            (r.status_zonas && camAtual.zona && r.status_zonas.toLowerCase() === camAtual.zona.toLowerCase())
        );

        const toDate = (v) => {
            if (!v) return null;
            if (v instanceof Date) return isNaN(v) ? null : v;
            if (typeof v === "string") {
                if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
                    const d = new Date(v.replace(" ", "T"));
                    return isNaN(d) ? null : d;
                }
                const d = new Date(v);
                return isNaN(d) ? null : d;
            }
            return null;
        };

        const N_SEGMENTS = 144; // 144 faixas de 10 min por dia
        const yLabels = Array.from({ length: N_SEGMENTS }, (_, i) => {
            const startMinutes = i * 10;
            const endMinutes = startMinutes + 10;
            const pad = n => String(n).padStart(2, '0');
            const sh = Math.floor(startMinutes / 60), sm = startMinutes % 60;
            const eh = Math.floor(endMinutes / 60), em = endMinutes % 60;
            return `${pad(sh)}:${pad(sm)}-${pad(eh)}:${pad(em)}`;
        });

        // Dias do mês atual
        const ano = parseInt(anoAtual, 10);
        const mes = parseInt(mesAtual, 10);
        const diasNoMes = new Date(ano, mes, 0).getDate();
        const diasArray = Array.from({ length: diasNoMes }, (_, i) => String(i + 1).padStart(2, '0'));

        const dataHeatmap = [];
        const agora = new Date();
        const diaHoje = agora.getDate();
        const mesHoje = agora.getMonth() + 1;
        const anoHoje = agora.getFullYear();
        const minutoAtual = agora.getHours() * 60 + agora.getMinutes();

        diasArray.forEach((diaStr, xIndex) => {
            const diaNum = parseInt(diaStr, 10);
            const ehHoje = (diaNum === diaHoje && mes === mesHoje && ano === anoHoje);
            const ehFuturo = (ano > anoHoje) || (ano === anoHoje && mes > mesHoje) || (ehHoje && false);

            const registrosDia = registrosCam.filter(r => {
                const d = toDate(r.data_hora_registro);
                return d && d.getDate() === diaNum && (d.getMonth() + 1) === mes && d.getFullYear() === ano;
            });

            for (let seg = 0; seg < N_SEGMENTS; seg++) {
                const segStart = seg * 10;
                const segEnd = segStart + 10;

                if (ehHoje && segStart > minutoAtual) {
                    dataHeatmap.push([xIndex, seg, null]);
                    continue;
                }
                if (ehFuturo) {
                    dataHeatmap.push([xIndex, seg, null]);
                    continue;
                }

                // Encontrar se houve auditoria nessa faixa de 10 minutos
                const regFaixa = registrosDia.find(r => {
                    const d = toDate(r.data_hora_registro);
                    if (!d) return false;
                    const m = d.getHours() * 60 + d.getMinutes();
                    return m >= segStart && m < segEnd;
                });

                let valor = null;
                if (regFaixa) {
                    if (regFaixa.ping_status === 1) {
                        valor = regFaixa.snapshot_status === 1 ? 2 : 1; // 2 = Online Completo, 1 = Falha Snapshot
                    } else {
                        valor = 0; // 0 = Offline
                    }
                }
                dataHeatmap.push([xIndex, seg, valor]);
            }
        });

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRefCamera.current);
        }

        const option = {
            tooltip: {
                position: 'top',
                formatter: (params) => {
                    const statusMap = {
                        0: "🔴 Offline (Falha de Ping)",
                        1: "🟡 Falha Snapshot Canal 1 (Ping OK)",
                        2: "🟢 Online (Ping OK + Snapshot OK)"
                    };
                    const dia = diasArray[params.value[0]];
                    const faixa = yLabels[params.value[1]];
                    const st = statusMap[params.value[2]] || "Sem registro nesta faixa";
                    return `<b>Dia ${dia}/${mesAtual}</b><br/>Faixa: ${faixa}<br/>Status: ${st}`;
                }
            },
            grid: { top: 30, bottom: 40, left: "80px", right: "20px" },
            xAxis: {
                type: "category",
                data: diasArray,
                name: "Dia",
                boundaryGap: true,
                axisLabel: { fontSize: 11 }
            },
            yAxis: {
                type: "category",
                data: yLabels,
                inverse: true,
                name: "Faixas 10min",
                axisLabel: { fontSize: 10 }
            },
            visualMap: {
                show: false,
                min: 0,
                max: 2,
                inRange: {
                    color: ["#FF0000", "#FFA500", "#00B050"]
                }
            },
            series: [{
                type: "heatmap",
                data: dataHeatmap,
                emphasis: { disabled: true },
                progressive: 10000
            }]
        };

        chartInstance.current.setOption(option, true);

        const handleResize = () => {
            if (chartInstance.current) chartInstance.current.resize();
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);

    }, [cameraSelecionadaId, cameras, registrosMes, mesAtual, anoAtual]);

    const cameraAtivaObj = cameras.find(c => c.id.toString() === cameraSelecionadaId.toString()) || cameras[0];

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999
        }}>
            <div style={{
                backgroundColor: "#ffffff",
                color: "#1e293b",
                width: "95%",
                height: "95%",
                borderRadius: "8px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.3)",
                display: "flex",
                flexDirection: "column",
                padding: "16px",
                position: "relative",
                overflowY: "auto",
                fontFamily: "'Roboto', 'Segoe UI', sans-serif"
            }}>
                {/* Botão Fechar (Padrão idêntico ao GraficoDisponibilidade) */}
                <button
                    onClick={onClose}
                    style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        backgroundColor: "#FF4500",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        cursor: "pointer",
                        fontWeight: "bold",
                        zIndex: 10
                    }}
                >
                    Fechar
                </button>

                {/* Card de Mês e Ano (Idêntico ao GraficoDisponibilidade / PGM) */}
                <Grid container justifyContent="center" style={{ marginBottom: "16px" }}>
                    <Card style={{
                        height: '90px',
                        padding: '4px 16px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        textAlign: 'center',
                        boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
                        backgroundColor: '#1976d2',
                        borderRadius: '8px'
                    }}>
                        <CardContent style={{ padding: 4 }}>
                            <TextField
                                variant="standard"
                                value={mesAtual}
                                onChange={(e) => setMesAtual(e.target.value)}
                                inputProps={{
                                    style: { textAlign: 'center', fontSize: '1.8rem', fontWeight: 'bold', color: '#fff', width: '60px' },
                                    maxLength: 2
                                }}
                                InputProps={{ disableUnderline: true }}
                            />
                            <TextField
                                variant="standard"
                                value={anoAtual}
                                onChange={(e) => setAnoAtual(e.target.value)}
                                inputProps={{
                                    style: { textAlign: 'center', fontSize: '0.95rem', color: '#e0e0e0', letterSpacing: '1px', width: '70px' },
                                    maxLength: 4
                                }}
                                InputProps={{ disableUnderline: true }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                <h5 style={{ margin: "0 0 16px 0", textAlign: "center", color: "#1e293b", fontWeight: 700 }}>
                    Disponibilidade de Câmeras — Módulo Unifier
                </h5>

                {loading ? (
                    <div style={{ textAlign: "center", marginTop: "60px", color: "#64748b" }}>
                        <p>Carregando histórico de disponibilidade das câmeras...</p>
                    </div>
                ) : error ? (
                    <div style={{ textAlign: "center", marginTop: "60px", color: "#ef4444" }}>{error}</div>
                ) : cameras.length === 0 ? (
                    <div style={{ textAlign: "center", marginTop: "60px", color: "#64748b" }}>
                        <p>Nenhuma câmera auditada encontrada para esta instalação neste período.</p>
                    </div>
                ) : (
                    <>
                        {/* Resumo Geral (Estilo PGM) */}
                        <div style={{
                            width: "96%",
                            margin: "0 auto 20px auto",
                            display: "flex",
                            justifyContent: "center",
                            gap: "24px",
                            flexWrap: "wrap",
                            padding: "12px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            textAlign: "center"
                        }}>
                            <div>
                                <span style={{ fontSize: "12px", color: "#64748b" }}>Disponibilidade Geral:</span>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#00B050" }}>{resumoGeral.pctOnline}% Online</div>
                            </div>
                            <div style={{ borderLeft: "1px solid #cbd5e1", paddingLeft: "24px" }}>
                                <span style={{ fontSize: "12px", color: "#64748b" }}>Tempo Offline:</span>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#ef4444" }}>{resumoGeral.pctOffline}%</div>
                            </div>
                            <div style={{ borderLeft: "1px solid #cbd5e1", paddingLeft: "24px" }}>
                                <span style={{ fontSize: "12px", color: "#64748b" }}>Latência Média:</span>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#1976d2" }}>{resumoGeral.latenciaMedia} ms</div>
                            </div>
                            <div style={{ borderLeft: "1px solid #cbd5e1", paddingLeft: "24px" }}>
                                <span style={{ fontSize: "12px", color: "#64748b" }}>Câmeras Monitoradas:</span>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#334155" }}>{cameras.length}</div>
                            </div>
                        </div>

                        {/* Tabela de Análise das Câmeras (Igual à Análise de Zonas do PGM) */}
                        <div style={{
                            width: "96%",
                            margin: "0 auto 24px auto",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "16px",
                            backgroundColor: "#ffffff"
                        }}>
                            <h5 style={{ margin: "0 0 12px 0", color: "#334155", borderBottom: "1px solid #f1f5f9", paddingBottom: "8px" }}>
                                Desempenho Individual por Câmera
                            </h5>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", textAlign: "left" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0" }}>ID</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0" }}>Zona / Câmera</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0" }}>IP</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>Protocolo</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", color: "#00B050" }}>🟢 % Online</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", color: "#ef4444" }}>🔴 % Offline</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>Latência</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>Último Registro</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>Último Snapshot</th>
                                            <th style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {estatisticasCameras.map((cam, idx) => {
                                            const isSelected = cam.id.toString() === cameraSelecionadaId.toString();
                                            const ult = cam.ultimoRegistro;
                                            const dtInfo = formatarDataHoraRelativa(ult?.data_hora_registro);
                                            const hasSnap = ult && (ult.caminho_imagem || ult.nome_imagem || ult.snapshot_status === 1);
                                            const apiBase = (api && api.defaults && api.defaults.baseURL) ? api.defaults.baseURL.replace(/\/$/, '') : (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/$/, '') : '');
                                            const snapUrl = ult && ult.id_registro_disponibilidade
                                                ? `${apiBase}/v1/protected/registrodisponibilidade/imagem/${ult.id_registro_disponibilidade}`
                                                : null;

                                            return (
                                                <tr
                                                    key={cam.id}
                                                    style={{
                                                        backgroundColor: isSelected ? "#eff6ff" : (idx % 2 === 0 ? "#ffffff" : "#f8fafc")
                                                    }}
                                                >
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", color: "#64748b" }}>#{cam.id}</td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", fontWeight: "600", color: "#1e293b" }}>
                                                        {cam.zona || `Câmera ${cam.ip}`}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", fontFamily: "monospace", color: "#0284c7" }}>
                                                        {cam.ip}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                        {cam.protocolo === 10 ? "Intelbras" : "Hikvision"}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", color: "#00B050", fontWeight: "bold" }}>
                                                        {cam.pctOnline}%
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", color: "#ef4444", fontWeight: "bold" }}>
                                                        {cam.pctOffline}%
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", color: "#475569" }}>
                                                        {cam.latenciaMedia !== '-' ? `${cam.latenciaMedia} ms` : '-'}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center", whiteSpace: "nowrap" }}>
                                                        {ult && ult.data_hora_registro ? (
                                                            <div>
                                                                <div style={{ fontWeight: "600", color: "#1e293b", fontSize: "12px" }}>
                                                                    {dtInfo.dataFormatada}
                                                                </div>
                                                                {dtInfo.tempoRelativo && (
                                                                    <div style={{ color: "#64748b", fontSize: "11px", marginTop: "2px" }}>
                                                                        ⏱️ {dtInfo.tempoRelativo}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: "#94a3b8", fontSize: "11px" }}>Sem registros</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                        {hasSnap && snapUrl ? (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                style={{ textTransform: "none", fontSize: "11px", padding: "2px 8px" }}
                                                                onClick={() => setFotoModal({
                                                                    url: snapUrl,
                                                                    titulo: `${cam.zona || cam.ip} — Canal 1`,
                                                                    dataHora: dtInfo.dataFormatada,
                                                                    tempoRelativo: dtInfo.tempoRelativo
                                                                })}
                                                            >
                                                                📷 Ver Foto
                                                            </Button>
                                                        ) : (
                                                            <span style={{ color: "#94a3b8", fontSize: "11px" }}>Sem foto</span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: "10px", border: "1px solid #e2e8f0", textAlign: "center" }}>
                                                        <Button
                                                            size="small"
                                                            variant={isSelected ? "contained" : "outlined"}
                                                            color="primary"
                                                            style={{ textTransform: "none", fontSize: "11px", padding: "2px 10px" }}
                                                            onClick={() => setCameraSelecionadaId(cam.id.toString())}
                                                        >
                                                            {isSelected ? "Visualizando" : "Ver Gráfico"}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Gráfico ECharts Heatmap (Idêntico ao do PGM) */}
                        <div style={{
                            width: "96%",
                            margin: "0 auto 24px auto",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "16px",
                            backgroundColor: "#ffffff"
                        }}>
                            <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
                                <div>
                                    <h4 style={{ margin: "0 0 4px 0", color: "#1e293b" }}>
                                        Gráfico de Disponibilidade: {cameraAtivaObj ? (cameraAtivaObj.zona || cameraAtivaObj.ip) : ''}
                                    </h4>
                                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                                        Faixas de 10 minutos registradas pelo serviço de auditoria ao longo do mês.
                                    </span>
                                </div>
                                <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
                                    <InputLabel id="select-cam-label">Selecione a Câmera</InputLabel>
                                    <Select
                                        labelId="select-cam-label"
                                        value={cameraSelecionadaId}
                                        onChange={(e) => setCameraSelecionadaId(e.target.value)}
                                        label="Selecione a Câmera"
                                    >
                                        {cameras.map(c => (
                                            <MenuItem key={c.id} value={c.id.toString()}>
                                                {c.zona || `Câmera ${c.ip}`} ({c.ip})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Contêiner do Gráfico ECharts */}
                            <div ref={chartRefCamera} style={{ width: "100%", height: "450px" }} />

                            {/* Legenda (Igual ao PGM) */}
                            <div style={{ marginTop: "16px", display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center", fontSize: "13px", color: "#475569" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <div style={{ backgroundColor: "#00B050", width: 16, height: 16, borderRadius: "2px" }}></div>
                                    <span>Online (Ping & Snapshot OK)</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <div style={{ backgroundColor: "#FFA500", width: 16, height: 16, borderRadius: "2px" }}></div>
                                    <span>Alerta Snapshot (Ping OK / Falha Imagem)</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <div style={{ backgroundColor: "#FF0000", width: 16, height: 16, borderRadius: "2px" }}></div>
                                    <span>Offline (Host Inacessível)</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Dialog / Lightbox para Snapshot Canal 1 */}
                <Dialog
                    open={Boolean(fotoModal)}
                    onClose={() => setFotoModal(null)}
                    maxWidth="md"
                    fullWidth
                    style={{ zIndex: 100005 }}
                >
                    <DialogTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}>
                        <div>
                            <Typography variant="h6" style={{ fontSize: "16px", fontWeight: "bold", color: "#1e293b" }}>
                                {fotoModal?.titulo || "Snapshot Canal 1"}
                            </Typography>
                            {fotoModal?.dataHora && (
                                <Typography variant="caption" style={{ color: "#64748b", display: "block" }}>
                                    📅 Capturado em: <strong>{fotoModal.dataHora}</strong> {fotoModal.tempoRelativo ? `(${fotoModal.tempoRelativo})` : ''}
                                </Typography>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            {fotoModal?.url && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    style={{ textTransform: "none", fontSize: "12px" }}
                                    onClick={() => window.open(fotoModal.url, '_blank')}
                                >
                                    ↗ Abrir Imagem Direta
                                </Button>
                            )}
                            <IconButton size="small" onClick={() => setFotoModal(null)}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </DialogTitle>
                    <DialogContent style={{ padding: "0 20px 20px 20px", textAlign: "center" }}>
                        {fotoModal && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginTop: "10px" }}>
                                <img
                                    src={fotoModal.url}
                                    alt="Snapshot Canal 1"
                                    style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: "6px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                        const errEl = document.getElementById('erro-foto-dialog');
                                        if (errEl) errEl.style.display = 'block';
                                    }}
                                />
                                <div id="erro-foto-dialog" style={{ display: "none", padding: "20px", color: "#64748b" }}>
                                    <p style={{ fontWeight: 600, color: "#ef4444" }}>Não foi possível carregar a imagem diretamente.</p>
                                    <p style={{ fontSize: "12px" }}>Você pode tentar abrir o link diretamente em uma nova aba:</p>
                                    <a href={fotoModal.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2", textDecoration: "underline" }}>
                                        {fotoModal.url}
                                    </a>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
};

export default DisponibilidadeModuloSonoro;
